import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHedera } from '../../contexts/HederaContext.js';
import './NFTList.css';

// Memoized NFT Item Component
const NFTItem = React.memo(({ nft, onSelect, renderNFTImage }) => {
    const handleClick = () => {
        const nftData = {
            token_id: nft.token_id,
            serial_number: nft.serial_number,
            isModel: nft.isModel,
            modelUrl: nft.modelUrl,
            metadata: nft.metadata,
            url: nft.url || nft.modelUrl || nft.metadata?.image,
            type: 'nft'
        };
        
        onSelect(nftData, false, true);
    };

    return (
        <div className="nft-item" onClick={handleClick}>
            <div className="nft-image-container">
                {renderNFTImage(nft)}
            </div>
            <div className="nft-info">
                <div className="nft-name">
                    {nft.metadata?.name || `NFT #${nft.serial_number}`}
                </div>
                <div className="nft-token-id">
                    {nft.token_id}
                </div>
            </div>
        </div>
    );
});

function NFTList({ onSelect }) {
    const { nfts, collections, isLoadingNfts, isLoadingMore, hasMore, loadMoreNFTs } = useHedera();
    const gridRef = useRef(null);
    const [reachedEnd, setReachedEnd] = useState(false);
    const [lastClickedNFT, setLastClickedNFT] = useState(null);
    const [currentClickData, setCurrentClickData] = useState(null);
    const [activeTab, setActiveTab] = useState('nfts'); // 'nfts' or 'collections'
    const [selectedCollection, setSelectedCollection] = useState(null); // Store selected collection ID
    const [searchTerm, setSearchTerm] = useState('');
    const [currentFileIndexes, setCurrentFileIndexes] = useState({}); // Track current file index per NFT

    // Debounced scroll handler
    const handleScroll = useCallback(() => {
        const grid = gridRef.current;
        if (!grid) return;

        // Only trigger when very close to bottom (90% of the way)
        if (grid.scrollHeight - grid.scrollTop <= grid.clientHeight + 100) {
            if (hasMore && !isLoadingMore) {
                loadMoreNFTs();
            } else if (!hasMore && !reachedEnd) {
                setReachedEnd(true);
            }
        }
    }, [hasMore, isLoadingMore, loadMoreNFTs, reachedEnd]);

    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        let timeoutId = null;
        const debouncedScroll = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(handleScroll, 150); // 150ms debounce
        };

        grid.addEventListener('scroll', debouncedScroll);
        return () => {
            grid.removeEventListener('scroll', debouncedScroll);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [handleScroll]);

    const convertHcsUrl = async (url) => {
        if (!url) return null;
        const match = url.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
        if (match) {
            try {
                const response = await fetch(`https://kiloscribe.com/api/inscription-cdn/${match[2]}`);
                if (response.ok) {
                    // Check for GLB data
                    const content = await response.text();
                    if (content.startsWith('glTF')) {
                        const blob = await response.blob();
                        return URL.createObjectURL(blob);
                    }
                    try {
                        // Try to parse as JSON
                        const metadata = JSON.parse(content);
                        return metadata.image || content;
                    } catch {
                        // If not JSON, return content directly
                        return content;
                    }
                }
            } catch (err) {
                console.error('Error fetching HCS content:', err);
            }
            return null;
        }
        return url;
    };

    // Helper functions for multi-file navigation
    const getNFTKey = (nft) => `${nft.token_id}-${nft.serial_number}`;
    
    const getCurrentFileIndex = (nft) => {
        const key = getNFTKey(nft);
        return currentFileIndexes[key] || 0;
    };
    
    const getTotalFiles = (nft) => {
        // Main content (from metadata.image) + files array
        return 1 + (nft.files?.length || 0);
    };
    
    const getCurrentFile = (nft) => {
        const currentIndex = getCurrentFileIndex(nft);
        
        // Index 0 = main content from metadata.image
        if (currentIndex === 0) {
            return {
                uri: nft.metadata.image,
                type: nft.metadata.type,
                isMain: true
            };
        }
        
        // Index 1+ = files from files array
        const fileIndex = currentIndex - 1;
        const file = nft.files?.[fileIndex];
        return file ? { ...file, isMain: false } : null;
    };
    
    const handlePreviousFile = useCallback((nft) => {
        const key = getNFTKey(nft);
        const currentIndex = getCurrentFileIndex(nft);
        const totalFiles = getTotalFiles(nft);
        const newIndex = currentIndex === 0 ? totalFiles - 1 : currentIndex - 1;
        
        setCurrentFileIndexes(prev => ({
            ...prev,
            [key]: newIndex
        }));
    }, []);
    
    const handleNextFile = useCallback((nft) => {
        const key = getNFTKey(nft);
        const currentIndex = getCurrentFileIndex(nft);
        const totalFiles = getTotalFiles(nft);
        const newIndex = (currentIndex + 1) % totalFiles;
        
        setCurrentFileIndexes(prev => ({
            ...prev,
            [key]: newIndex
        }));
    }, []);

    const renderNFTImage = useCallback((nft) => {
        // For thumbnails, always show the first file (index 0)
        const firstFile = {
            uri: nft.metadata.image,
            type: nft.metadata.type,
            isMain: true
        };
        const totalFiles = getTotalFiles(nft);
        

        if (!nft.metadata?.image && !nft.decodedMetadata) {
            return (
                <div className="nft-placeholder">
                    🖼️
                </div>
            );
        }

        // Determine first file type for thumbnail
        const isFirstFileModel = firstFile?.type?.includes('model') || nft.isModel;
        const isFirstFileVideo = firstFile?.type?.startsWith('video/') || nft.isVideo;
        const isFirstFileHTML = firstFile?.type === 'text/html';

        // Handle 3D models (GLB/GLTF)
        if (isFirstFileModel) {
            return (
                <div className="nft-placeholder model">
                    <div className="cube-icon">
                        <div className="cube-face front"></div>
                        <div className="cube-face back"></div>
                        <div className="cube-face right"></div>
                        <div className="cube-face left"></div>
                        <div className="cube-face top"></div>
                        <div className="cube-face bottom"></div>
                    </div>
                    <div className="model-indicator">3D</div>
                    {totalFiles > 1 && (
                        <div className="multi-file-badge">
                            {totalFiles} FILES
                        </div>
                    )}
                </div>
            );
        }

        // Handle videos
        if (isFirstFileVideo) {
            return (
                <div className="nft-video-container">
                    <video
                        src={firstFile.uri}
                        className="nft-video"
                        controls
                        muted
                        preload="metadata"
                        onError={(e) => {
                            console.error('Video playback error:', e);
                        }}
                    >
                        Your browser does not support the video tag.
                    </video>
                    <div className="video-indicator">VIDEO</div>
                    {totalFiles > 1 && (
                        <div className="multi-file-badge">
                            {totalFiles} FILES
                        </div>
                    )}
                </div>
            );
        }

        // Handle HTML content (temporarily disabled for performance)
        if (isFirstFileHTML) {
            return (
                <div className="nft-placeholder html">
                    <div className="html-icon">📄</div>
                    <div className="html-indicator">HTML</div>
                    {totalFiles > 1 && (
                        <div className="multi-file-badge">
                            {totalFiles} FILES
                        </div>
                    )}
                </div>
            );
        }

        if (nft.decodedMetadata) {
            // For SVG content
            // Clean up SVG content to ensure it has viewBox and preserveAspectRatio
            let svgContent = nft.decodedMetadata;
            if (!svgContent.includes('viewBox')) {
                // Extract width and height if they exist
                const widthMatch = svgContent.match(/width="([^"]*)"/);
                const heightMatch = svgContent.match(/height="([^"]*)"/);
                if (widthMatch && heightMatch) {
                    const viewBox = `0 0 ${widthMatch[1]} ${heightMatch[1]}`;
                    svgContent = svgContent.replace('<svg', `<svg viewBox="${viewBox}"`);
                }
            }
            // Add preserveAspectRatio if it doesn't exist
            if (!svgContent.includes('preserveAspectRatio')) {
                svgContent = svgContent.replace('<svg', '<svg preserveAspectRatio="xMidYMid meet"');
            }

            return (
                <div className="nft-svg-content">
                    <div className="nft-svg-wrapper">
                        <div
                            dangerouslySetInnerHTML={{ __html: svgContent }}
                        />
                    </div>
                </div>
            );
        }

        // For regular images, handle HCS URLs
        const ImageComponent = () => {
            const [imageUrl, setImageUrl] = useState(null);
            const [hasError, setHasError] = useState(false);
            const isMounted = useRef(true);

            useEffect(() => {
                isMounted.current = true;
                return () => {
                    isMounted.current = false;
                };
            }, []);

            useEffect(() => {
                const loadImage = async () => {
                    try {
                        const url = await convertHcsUrl(firstFile.uri);
                        if (isMounted.current) {
                            setImageUrl(url);
                        }
                    } catch (error) {
                        console.error('Error loading image:', error);
                        if (isMounted.current) {
                            setHasError(true);
                        }
                    }
                };
                loadImage();

                return () => {
                    // Cleanup any blob URLs to prevent memory leaks
                    if (imageUrl?.startsWith('blob:')) {
                        URL.revokeObjectURL(imageUrl);
                    }
                };
            }, [firstFile.uri]);

            if (hasError) {
                return (
                    <div className="nft-placeholder">
                        🖼️
                    </div>
                );
            }

            if (!imageUrl) {
                return (
                    <div className="nft-placeholder">
                        <div className="loading-spinner"></div>
                    </div>
                );
            }

            return (
                <div className="nft-image-wrapper">
                    <img
                        src={imageUrl}
                        alt={nft.metadata.name || 'NFT'}
                        className="nft-thumbnail"
                        onError={() => {
                            console.error('Failed to load image:', imageUrl);
                            setHasError(true);
                        }}
                    />
                    {totalFiles > 1 && (
                        <div className="multi-file-badge">
                            {totalFiles} FILES
                        </div>
                    )}
                </div>
            );
        };

        return <ImageComponent />;
    }, []);

    // Add effect to verify data persistence
    useEffect(() => {
        if (currentClickData && lastClickedNFT) {
        }
    }, [lastClickedNFT, currentClickData]);

    // Filter NFTs based on search term
    const filterNFTs = (nftList) => {
        if (!searchTerm.trim()) return nftList;
        
        return nftList.filter(nft => {
            const name = nft.metadata?.name || `NFT #${nft.serial_number}`;
            return name.toLowerCase().includes(searchTerm.toLowerCase().trim());
        });
    };

    // Get the NFTs for the selected collection
    const getCollectionNfts = () => {
        if (!selectedCollection || !collections[selectedCollection]) {
            return [];
        }
        const collectionNfts = collections[selectedCollection].nfts || [];
        return filterNFTs(collectionNfts);
    };

    // Get filtered NFTs for the main view
    const getFilteredNFTs = () => {
        return filterNFTs(nfts);
    };

    // Reset selected collection when changing tabs
    useEffect(() => {
        if (activeTab !== 'collections') {
            setSelectedCollection(null);
        }
    }, [activeTab]);

    if (isLoadingNfts) {
        return (
            <div className="nft-list loading">
                <div className="nft-list-header">
                    <h3>Your NFTs</h3>
                </div>
                <div className="loading-grid">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="loading-item">
                            <div className="loading-image-container">
                                <div className="loading-spinner" />
                            </div>
                            <div className="loading-info" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Get collections as a simple array for the collections tab
    const collectionArray = Object.entries(collections || {}).map(([tokenId, collection]) => ({
        id: tokenId,
        name: collection.name || `Collection ${tokenId}`,
        count: collection.nfts?.length || 0
    }));

    // Debug logs to understand what's happening

    // If collections is empty but we have NFTs, let's create collections on the fly as a fallback
    if (collectionArray.length === 0 && nfts.length > 0) {
        console.log('Creating collections on the fly as fallback');
        
        // Group NFTs by token_id as a fallback
        const fallbackCollections = {};
        nfts.forEach(nft => {
            const tokenId = nft.token_id;
            if (!tokenId) return;
            
            if (!fallbackCollections[tokenId]) {
                fallbackCollections[tokenId] = {
                    id: tokenId,
                    name: `Collection ${tokenId}`,
                    count: 0,
                    nfts: []
                };
            }
            
            fallbackCollections[tokenId].nfts.push(nft);
            fallbackCollections[tokenId].count = fallbackCollections[tokenId].nfts.length;
        });
        
        // Convert to array for rendering
        const fallbackArray = Object.values(fallbackCollections);
        console.log('Fallback collections created:', fallbackArray);
        
        // Use fallback if available, else use original (empty) array
        if (fallbackArray.length > 0) {
            return (
                <div className="nft-list">
                    {/* Tab Navigation */}
                    <div className="nft-tabs">
                        <button 
                            className={`tab-button ${activeTab === 'nfts' ? 'active' : ''}`}
                            onClick={() => setActiveTab('nfts')}
                        >
                            All NFTs
                        </button>
                        <button 
                            className={`tab-button ${activeTab === 'collections' ? 'active' : ''}`}
                            onClick={() => setActiveTab('collections')}
                        >
                            Collections
                        </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'nfts' ? (
                        <>
                            <div className="nft-list-header">
                                <div>
                                    <h3>Your NFTs</h3>
                                    {nfts.length > 0 && (
                                        <span className="nft-count">({getFilteredNFTs().length} of {nfts.length})</span>
                                    )}
                                </div>
                                <div className="search-container">
                                    <input
                                        type="text"
                                        placeholder="Search NFTs..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="nft-search-input"
                                    />
                                </div>
                            </div>
                            <div className="nft-grid" ref={gridRef}>
                                {getFilteredNFTs().map((nft, index) => (
                                    <NFTItem
                                        key={`${nft.token_id}-${nft.serial_number}`}
                                        nft={nft}
                                        onSelect={(nftData, capture, isNft) => {
                                            setCurrentClickData(nftData);
                                            setLastClickedNFT(nftData);
                                            onSelect(nftData, capture, isNft);
                                        }}
                                        renderNFTImage={renderNFTImage}
                                    />
                                ))}
                                {isLoadingMore && (
                                    <div className="loading-more">
                                        <div className="loading-spinner"></div>
                                    </div>
                                )}
                                {reachedEnd && !isLoadingMore && (
                                    <div className="end-of-list">
                                        <span>End of list - No more NFTs to load</span>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="nft-list-header">
                                <div>
                                    <h3>NFT Collections (Fallback)</h3>
                                    <span className="nft-count">({fallbackArray.length} collections)</span>
                                </div>
                            </div>
                            <div className="collections-list" ref={gridRef}>
                                {fallbackArray.map(collection => (
                                    <div 
                                        key={collection.id} 
                                        className="collection-list-item"
                                        onClick={() => {
                                            console.log('Collection clicked:', collection);
                                        }}
                                    >
                                        <div className="collection-name">{collection.name}</div>
                                        <div className="collection-count">{collection.count} NFTs</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            );
        }
    }

    return (
        <div className="nft-list">
            {/* Tab Navigation */}
            <div className="nft-tabs">
                <button 
                    className={`tab-button ${activeTab === 'nfts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('nfts')}
                >
                    All NFTs
                </button>
                <button 
                    className={`tab-button ${activeTab === 'collections' ? 'active' : ''}`}
                    onClick={() => setActiveTab('collections')}
                >
                    Collections
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'nfts' ? (
                <>
                    <div className="nft-list-header">
                        <div>
                            <h3>Your NFTs</h3>
                            {nfts.length > 0 && (
                                <span className="nft-count">({getFilteredNFTs().length} of {nfts.length})</span>
                            )}
                        </div>
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search NFTs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="nft-search-input"
                            />
                        </div>
                    </div>
                    <div className="nft-grid" ref={gridRef}>
                        {getFilteredNFTs().map((nft, index) => (
                            <NFTItem
                                key={`${nft.token_id}-${nft.serial_number}`}
                                nft={nft}
                                onSelect={(nftData, capture, isNft) => {
                                    setCurrentClickData(nftData);
                                    setLastClickedNFT(nftData);
                                    onSelect(nftData, capture, isNft);
                                }}
                                renderNFTImage={renderNFTImage}
                            />
                        ))}
                        {isLoadingMore && (
                            <div className="loading-more">
                                <div className="loading-spinner"></div>
                            </div>
                        )}
                        {reachedEnd && !isLoadingMore && (
                            <div className="end-of-list">
                                <span>End of list - No more NFTs to load</span>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {selectedCollection ? (
                        // Collection Detail View - Show NFTs in the selected collection
                        <>
                            <div className="nft-list-header">
                                <div>
                                    <button 
                                        className="back-button"
                                        onClick={() => setSelectedCollection(null)}
                                    >
                                        ← Back
                                    </button>
                                    <h3>{collections[selectedCollection]?.name || `Collection ${selectedCollection}`}</h3>
                                    <span className="nft-count">
                                        ({getCollectionNfts().length} of {collections[selectedCollection]?.nfts?.length || 0} NFTs)
                                    </span>
                                </div>
                                <div className="search-container">
                                    <input
                                        type="text"
                                        placeholder="Search collection NFTs..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="nft-search-input"
                                    />
                                </div>
                            </div>
                            <div className="nft-grid" ref={gridRef}>
                                {getCollectionNfts().map((nft, index) => (
                                    <NFTItem
                                        key={`${nft.token_id}-${nft.serial_number}`}
                                        nft={nft}
                                        onSelect={(nftData, capture, isNft) => {
                                            console.log('Collection NFT clicked - Capturing data...', nftData);
                                            setCurrentClickData(nftData);
                                            setLastClickedNFT(nftData);
                                            onSelect(nftData, capture, isNft);
                                        }}
                                        renderNFTImage={renderNFTImage}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        // Collections List View
                        <>
                            <div className="nft-list-header">
                                <div>
                                    <h3>NFT Collections</h3>
                                    {collectionArray.length > 0 && (
                                        <span className="nft-count">({collectionArray.length} collections)</span>
                                    )}
                                </div>
                            </div>
                            <div className="collections-list" ref={gridRef}>
                                {collectionArray.length === 0 ? (
                                    <div className="collections-empty">
                                        No collections found
                                    </div>
                                ) : (
                                    collectionArray.map(collection => {
                                        // Find a representative NFT for the collection
                                        const representativeNft = collections[collection.id]?.nfts?.[0];
                                        
                                        return (
                                            <div 
                                                key={collection.id} 
                                                className="collection-list-item"
                                                onClick={() => {
                                                    console.log('Collection clicked:', collection);
                                                    setSelectedCollection(collection.id);
                                                }}
                                            >
                                                {/* Add thumbnail if a representative NFT is available */}
                                                {representativeNft && (
                                                    <div className="collection-thumbnail">
                                                        {renderNFTImage(representativeNft)}
                                                    </div>
                                                )}
                                                <div className="collection-info">
                                                    <div className="collection-name">{collection.name}</div>
                                                    <div className="collection-count">{collection.count} NFTs</div>
                                                    <div className="collection-id">{collection.id}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default React.memo(NFTList); 